<script setup lang="ts">
/**
 * SG — Incorporation (Post) Studio panel (B32110).
 * Live document package + L_ENTITY autofill (profile / directors / secretaries / members).
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { fetchSessionClientNm } from '@/features/startTask/preset_task/sg/composables/fetchSessionClientNm';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import {
  getActiveDirectors,
  getActiveSecretaries,
  getActiveMembers,
} from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { provideCddFill, bareKey, typeOf, isProgressFilled } from './incorporation-pre/cdd/useCddFill';
import SgIncorporationPostPackageDoc from './incorporation-post/SgIncorporationPostPackageDoc.vue';
import {
  provideSgPostSigners,
  SG_POST_SIGNER_KEYS,
  buildAllDirectorsSigners,
  shareIndSignersKey,
  shareCorpSignersKey,
  ddfSignersKey,
} from './incorporation-post/sgPostSignersContext';
import { mapSgEntityToPostValues } from '../mappers/mapSgEntityToPostValues';
import {
  buildScopedRepeatingRows,
  buildIndemnityDirectorRows,
  FIRST_RES_DIRECTOR_KEYS,
  FIRST_RES_ALLOTTEE_KEYS,
  INDEMNITY_DIRECTOR_KEYS,
  attachDdfRepeatingSections,
  attachNomineeRepeatingSections,
  attachWelcomeRepeatingSections,
} from '../utils/buildSgPostRepeatingRows';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import './incorporation-pre/allotDoc.css';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const sessionStore = useSessionStore();
const fill = provideCddFill();
const docRef = ref<InstanceType<typeof SgIncorporationPostPackageDoc> | null>(null);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);

/** Signers — L_ENTITY 기준 officer 선택 */
const signersCtx = provideSgPostSigners(entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const studio = computed(() => !!props.studio);
const creating = ref(false);
const entityLoading = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const lastAutofillEntity = ref('');
const autofilledKeys = ref<Set<string>>(new Set());
const pendingLocalRestore = ref<Record<string, any> | null>(null);

async function ensureCspLetterheadFilled() {
  const needName = !String(fill.values.csp_name || '').trim();
  const needUen = !String(fill.values.csp_uen || '').trim();
  if (!needName && !needUen) return;
  try {
    const customer = sessionStore.getCustomerInfo();
    // CSP UEN = 로그인 클라이언트 REG_NO (filing agent UEN과 동일)
    if (needUen) {
      const uen = String(customer?.regNo || '').trim();
      if (uen) fill.setValue('csp_uen', uen);
    }
    if (needName) {
      const nm =
        (await fetchSessionClientNm(String(customer?.code || '').trim())) ||
        String(customer?.name || '').trim();
      if (nm) fill.setValue('csp_name', nm);
    }
  } catch (e) {
    console.warn('[SgIncorporationPost] CSP letterhead inject failed:', e);
  }
}

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  if (pct < 100 && typeof console !== 'undefined') {
    const empty = fill.keys.value.filter((k) => !isProgressFilled(k, fill.values[k], fill.values));
    if (empty.length && empty.length <= 20) {
      console.debug('[SgIncorporationPost] progress empty keys', empty);
    }
  }
  emit('progress-change', pct);
}

watch([() => fill.filledCount.value, () => fill.totalFields.value], () => {
  emitProgress();
  signersCtx.seedFromValuesIfEmpty(fill.exportPayload());
});

/** Field key → officer role (Director / Secretary / Shareholder / Contact). */
function lookupForKey(bare: string, scopedKey: string): {
  defaultRole: string;
  lookupType?: string;
  memberEntityKind?: 'individual' | 'corporate';
  contactCategoryCd?: string;
  entityCd?: string;
} | null {
  // CSP letterhead name — session client (no chatbot). Signatory/witness may still look up contacts.
  if (bare === 'csp_name') return null;

  if (bare === 'company_name' || bare === 'entity_name') {
    return { defaultRole: '', lookupType: 'entity' };
  }

  // Corporate company / corporate shareholder → members (corporate) or corporate contacts
  if (
    bare === 'corporate_shareholder_name' ||
    bare === 'cs_company_name' ||
    /share_corp_/.test(scopedKey)
  ) {
    return {
      defaultRole: 'SHAREHOLDER',
      lookupType: 'officer',
      memberEntityKind: 'corporate',
      entityCd: entityCd.value || '',
    };
  }

  if (/secretary/.test(bare)) {
    return {
      defaultRole: 'SECRETARY',
      lookupType: 'officer',
      entityCd: entityCd.value || '',
    };
  }

  // FY Checklist auditor firm — AUDITOR register (not Director)
  if (/auditor/.test(bare)) {
    return {
      defaultRole: 'AUDITOR',
      lookupType: 'officer',
      entityCd: entityCd.value || '',
    };
  }

  // Bank name/branch — free text only (no contact chatbot)
  if (/^bank_/.test(bare)) return null;

  if (
    /shareholder|applicant|allottee|^a_name$|member_name|nsh_/.test(bare) ||
    /share_ind_|allot_/.test(scopedKey)
  ) {
    return {
      defaultRole: 'SHAREHOLDER',
      lookupType: 'officer',
      memberEntityKind: 'individual',
      entityCd: entityCd.value || '',
    };
  }

  // Welcome Letter contacts / CSP staff / witnesses — client-wide contacts
  if (/^c_\d+_name$/.test(bare) || /csp_signatory|csp_witness|witness_name/.test(bare)) {
    return {
      defaultRole: 'CONTACT',
      lookupType: 'officer',
      entityCd: '',
    };
  }

  // Directors & other person fields on the entity
  return {
    defaultRole: 'DIRECTOR',
    lookupType: 'officer',
    entityCd: entityCd.value || '',
  };
}

fill.setActivateHandler((key: string) => {
  const bare = bareKey(key);
  const t = typeOf(key);
  if (t === 'address') {
    emit('request-address-input', key);
    return;
  }
  if (t !== 'entity' && t !== 'person' && t !== 'ssic') return;

  const spec = lookupForKey(bare, key);
  if (!spec) return;

  emit('request-person-lookup', {
    fieldGroup: 'sg-incorporation-post',
    key: bare,
    scopedKey: key,
    defaultRole: spec.defaultRole || undefined,
    lookupType: spec.lookupType || 'officer',
    entityCd: spec.entityCd ?? entityCd.value ?? '',
    ...(spec.memberEntityKind ? { memberEntityKind: spec.memberEntityKind } : {}),
    ...(spec.contactCategoryCd ? { contactCategoryCd: spec.contactCategoryCd } : {}),
  });
});

fill.setAddressHandler((key: string) => {
  emit('request-address-input', key);
});

watch(
  () => props.appliedAddress,
  (a) => {
    if (!a?.key) return;
    const line = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join(', ');
    if (line) fill.setValue(a.key, line);
  },
);

watch(
  () => props.selectedPerson,
  async (p) => {
    if (!p) return;
    let person: Record<string, any> = { ...(p as any) };
    const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();
    if (contactCd) {
      try {
        const raw: any = await contactsApi.getContactById(contactCd);
        const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
        person = { ...c, ...person };
      } catch (err) {
        console.warn('[SgIncorporationPost] contact enrich failed:', err);
      }
    }
    await fill.applyPersonToActive(person);
    emitProgress();
  },
);

function flushLocalRestore() {
  const pending = pendingLocalRestore.value;
  if (!pending || !docRef.value?.restoreLocalState) return;
  docRef.value.restoreLocalState(pending);
}

function clearAutofilled() {
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  for (const k of Object.keys(fill.values)) {
    if (
      /^(dir_|allot_|indem_|ddf_|share_ind_|share_corp_)\d+\//.test(k) ||
      /^c_\d+_(name|contact_no|email)$/.test(k)
    ) {
      fill.setValue(k, '');
    }
  }
  autofilledKeys.value = new Set();
  lastAutofillEntity.value = '';
  signersCtx.importAll({});
}

async function autofillFromEntity(cd: string) {
  const entityCdVal = String(cd || '').trim();
  if (!entityCdVal || entityCdVal === lastAutofillEntity.value) return;
  if (editMode.value) return;

  if (autofilledKeys.value.size || lastAutofillEntity.value) clearAutofilled();
  lastAutofillEntity.value = entityCdVal;
  entityLoading.value = true;
  try {
    const [profileRes, dirRes, secRes, memRes] = await Promise.all([
      docGenApi.getEntityProfile(entityCdVal, 'SG').catch(() => null),
      getActiveDirectors(entityCdVal).catch(() => null),
      getActiveSecretaries(entityCdVal).catch(() => null),
      getActiveMembers(entityCdVal).catch(() => null),
    ]);
    await ensureCspLetterheadFilled();

    const mapped = mapSgEntityToPostValues({
      profile: (profileRes as any)?.data ?? profileRes,
      entityHint: props.entityData,
      directors: (dirRes as any)?.data?.directors || [],
      secretaries: (secRes as any)?.data?.secretaries || [],
      members: (memRes as any)?.data?.members || [],
      cspName: String(fill.values.csp_name || '').trim() || undefined,
    });

    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      if (v) {
        fill.setValue(k, v);
        written.add(k);
      }
    }
    autofilledKeys.value = written;
    pendingLocalRestore.value = mapped.local;
    await nextTick();
    flushLocalRestore();
    await nextTick();
    signersCtx.seedFromValuesIfEmpty(fill.exportPayload());
    emitProgress();
  } catch (err) {
    console.warn('[SgIncorporationPost] entity autofill failed:', err);
    lastAutofillEntity.value = '';
  } finally {
    entityLoading.value = false;
  }
}

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      if (prev && prev !== cd) lastAutofillEntity.value = '';
      void autofillFromEntity(cd);
    } else if (prev) {
      clearAutofilled();
      pendingLocalRestore.value = {
        shareIndIds: [],
        shareCorpIds: [],
        ddfIds: [1],
        includeNominee: false,
        includePaidUp: false,
        firstResDirectorIds: [1],
        firstResAllotteeIds: [1],
        indemnityIds: [1],
        welcomeContactIds: [1],
      };
      flushLocalRestore();
      pendingLocalRestore.value = null;
    }
  },
  { immediate: true },
);

watch(docRef, (el) => {
  if (el) void nextTick(() => flushLocalRestore());
});

onMounted(async () => {
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    const steps = cfg?.processSteps;
    taskConfigSteps.value = Array.isArray(steps) ? steps : [];
  } catch {
    /* ignore */
  }
  await ensureCspLetterheadFilled();
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}

function restoreState(s: any) {
  if (!s) return;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) {
      if (v != null) fill.setValue(k, String(v));
    }
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) {
    pendingLocalRestore.value = s.local;
    flushLocalRestore();
  }
}

const TASK_CODE = 'B32110';

function classifySgPostDoc(name: string): string {
  const n = String(name || '').toLowerCase();
  if (/welcome/.test(n)) return 'welcome';
  if (/first\s*director|directors?\s*resolution/.test(n)) return 'first_res';
  if (/form\s*a\s*1|form\s*a1/.test(n)) return 'form_a1';
  if (/share\s*application/.test(n) && /corporate|corp/.test(n)) return 'share_corp';
  if (/share\s*application/.test(n)) return 'share_ind';
  if (/paid\s*up|paid-up|confirmation.*capital/.test(n)) return 'paid_up';
  if (/corporate\s*secretarial|secretarial\s*service/.test(n)) return 'cosec_agr';
  if (/registered\s*address/.test(n)) return 'reg_addr';
  if (/nomination/.test(n)) return 'nomination';
  if (/indemnity/.test(n)) return 'indemnity';
  if (/nominee\s*director/.test(n)) return 'nominee';
  if (/ddf|disclosure\s*of\s*director/.test(n)) return 'ddf';
  if (/checklist|financial\s*year/.test(n)) return 'fy_check';
  return 'other';
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    await ensureCspLetterheadFilled();
    const valuesSnap = fill.exportPayload();
    signersCtx.seedFromValuesIfEmpty(valuesSnap);
    const baseForm: Record<string, any> = { ...valuesSnap };
    for (const [k, v] of Object.entries(valuesSnap)) {
      if (k.includes('/') && v) {
        const bare = bareKey(k);
        if (!baseForm[bare]) baseForm[bare] = v;
      }
    }
    // Welcome / Nomination Word header — Studio csp_* 키를 반드시 formData에 실음
    const CSP_LETTERHEAD_KEYS = [
      'csp_name',
      'csp_uen',
      'csp_address_line1',
      'csp_address_line2',
      'csp_phone',
      'csp_email',
      'csp_website',
      'csp_full_address',
    ] as const;
    for (const k of CSP_LETTERHEAD_KEYS) {
      const v = String(fill.values[k] ?? baseForm[k] ?? '').trim();
      if (v) baseForm[k] = v;
    }
    if (!String(baseForm.csp_full_address || '').trim()) {
      const composed = [baseForm.csp_address_line1, baseForm.csp_address_line2]
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .join(', ');
      if (composed) baseForm.csp_full_address = composed;
    }
    // Form A1 Word [[if rorc_location == "…"]] / rond_location
    if (!String(baseForm.rorc_location || '').trim()) baseForm.rorc_location = 'company_office';
    if (!String(baseForm.rond_location || '').trim()) baseForm.rond_location = 'company_office';
    const signersMap = signersCtx.exportAll();
    const allDirectorsFallback = buildAllDirectorsSigners(valuesSnap);

    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_INCORPORATION_POST',
      formData: baseForm,
      signers: signersMap,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Incorporation (Post)',
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';
    const docs = props.selection.documents || [];
    const local = docRef.value?.gatherLocalState?.() || pendingLocalRestore.value || {
      shareIndIds: [],
      shareCorpIds: [],
      ddfIds: [1],
      includeNominee: false,
      includePaidUp: false,
    };
    const companyLabel = String(baseForm.company_name || 'Company');

    function formDataForScope(scopePrefix: string) {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).includes('/')) out[k] = v;
      }
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).startsWith(scopePrefix)) continue;
        const bare = String(k).slice(scopePrefix.length);
        if (bare) out[bare] = v;
        out[k] = v;
      }
      return out;
    }

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    function signersForKind(kind: string, copyId?: number): any[] | undefined {
      if (kind === 'share_ind' && copyId != null) {
        const list = signersMap[shareIndSignersKey(copyId)];
        return list?.length ? list : undefined;
      }
      if (kind === 'share_corp' && copyId != null) {
        const list = signersMap[shareCorpSignersKey(copyId)];
        return list?.length ? list : undefined;
      }
      if (kind === 'ddf' && copyId != null) {
        const list = signersMap[ddfSignersKey(copyId)];
        return list?.length ? list : undefined;
      }

      const key =
        kind === 'first_res'
          ? SG_POST_SIGNER_KEYS.firstRes
          : kind === 'form_a1'
            ? SG_POST_SIGNER_KEYS.formA1
            : kind === 'paid_up'
              ? SG_POST_SIGNER_KEYS.paidUp
              : kind === 'cosec_agr'
                ? SG_POST_SIGNER_KEYS.cosecAgr
                : kind === 'reg_addr'
                  ? SG_POST_SIGNER_KEYS.regAddr
                  : kind === 'nominee'
                    ? SG_POST_SIGNER_KEYS.nominee
                    : kind === 'nomination'
                      ? SG_POST_SIGNER_KEYS.nomination
                      : kind === 'indemnity'
                        ? SG_POST_SIGNER_KEYS.indemnity
                        : '';
      if (!key) return undefined;
      const list = signersMap[key];
      if (list?.length) return list;
      // First Res only — 이사 전원 fallback
      if (kind === 'first_res' && allDirectorsFallback.length) return allDirectorsFallback;
      // 이사 1인 / 양 당사자 — 수동 지정 없으면 비움 (자동 넣지 않음)
      return undefined;
    }

    function pushOnce(
      d: any,
      formData: Record<string, any>,
      personName: string,
      labelSuffix = '',
      signers?: any[],
    ) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '');
      docsToSave.push({
        templateId: tid,
        label: labelSuffix ? `${baseLabel} — ${labelSuffix}` : baseLabel,
        personName,
        entityCd: entityCd.value || undefined,
        formData,
        signers: signers?.length ? signers : undefined,
      });
    }

    for (const d of docs) {
      const kind = classifySgPostDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'share_ind') {
        const ids = local.shareIndIds?.length ? local.shareIndIds : [];
        for (const id of ids) {
          const fd = formDataForScope(`share_ind_${id}/`);
          pushOnce(
            d,
            fd,
            String(fd.applicant_name || `Individual ${id}`),
            String(fd.applicant_name || id),
            signersForKind('share_ind', id),
          );
        }
        continue;
      }
      if (kind === 'share_corp') {
        const ids = local.shareCorpIds?.length ? local.shareCorpIds : [];
        for (const id of ids) {
          const fd = formDataForScope(`share_corp_${id}/`);
          pushOnce(
            d,
            fd,
            String(fd.corporate_shareholder_name || `Corporate ${id}`),
            String(fd.corporate_shareholder_name || id),
            signersForKind('share_corp', id),
          );
        }
        continue;
      }
      if (kind === 'ddf') {
        const ids = local.ddfIds?.length ? local.ddfIds : [1];
        for (const id of ids) {
          const fd = attachDdfRepeatingSections(formDataForScope(`ddf_${id}/`));
          pushOnce(
            d,
            fd,
            String(fd.director_name || `Director ${id}`),
            String(fd.director_name || id),
            signersForKind('ddf', id),
          );
        }
        continue;
      }
      if (kind === 'indemnity') {
        // Word [[if directors]] — Studio indem_N/* → directors[] (증인 키 포함·빈값 명시)
        const fd: Record<string, any> = { ...baseForm };
        const directors = buildIndemnityDirectorRows(baseForm);
        fd.directors = directors;
        // 루프 플레이스홀더와 충돌하는 bare 키 제거 (전역 치환 오염 방지)
        for (const k of INDEMNITY_DIRECTOR_KEYS) delete fd[k];
        pushOnce(d, fd, companyLabel, '', signersForKind(kind));
        continue;
      }
      if (kind === 'first_res') {
        // Word [[if directors]] / [[if allottees]]
        const fd: Record<string, any> = { ...baseForm };
        const directors = buildScopedRepeatingRows(baseForm, 'dir_', [...FIRST_RES_DIRECTOR_KEYS]);
        const allottees = buildScopedRepeatingRows(baseForm, 'allot_', [...FIRST_RES_ALLOTTEE_KEYS]);
        if (directors.length) fd.directors = directors;
        if (allottees.length) fd.allottees = allottees;
        for (const k of FIRST_RES_DIRECTOR_KEYS) delete fd[k];
        for (const k of FIRST_RES_ALLOTTEE_KEYS) delete fd[k];
        pushOnce(d, fd, companyLabel, '', signersForKind(kind));
        continue;
      }
      if (kind === 'nominee') {
        if (local.includeNominee === false) continue;
        // Word [[if ap|principals|directors_list|shareholders_list|authorised_persons_table]]
        pushOnce(
          d,
          attachNomineeRepeatingSections({ ...baseForm }),
          companyLabel,
          '',
          signersForKind(kind),
        );
        continue;
      }
      if (kind === 'welcome') {
        // Word [[if client_contacts]]
        pushOnce(
          d,
          attachWelcomeRepeatingSections({ ...baseForm }),
          companyLabel,
          '',
          signersForKind(kind),
        );
        continue;
      }
      if (kind === 'paid_up' && local.includePaidUp === false) continue;
      pushOnce(d, { ...baseForm }, companyLabel, '', signersForKind(kind));
    }

    if (docsToSave.length) {
      const prep = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId,
          documents: docsToSave,
          replaceExisting: true,
        }),
      }).then((r) => r.json()).catch((e) => ({ success: false, error: e }));
      if (!prep?.success) {
        console.warn('[SgIncorporationPost] prepare-documents:', prep);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[SgIncorporationPost] create failed', e);
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sg-inc-post-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Incorporation (Post)' }}</div>
        <div class="co-subtitle">SG Incorporation (Post) — live document package (L_ENTITY)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change entity</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field
        v-model="titleModel"
        density="compact"
        variant="outlined"
        hide-details
        label="Task Title"
        placeholder="e.g. SG Incorporation (Post) — Acme Pte. Ltd."
      />
    </div>

    <div v-if="!studio && entityCd" class="co-entity">
      Linked entity: <strong>{{ entityData?.entityName || entityData?.companyName || entityCd }}</strong>
      <span v-if="entityLoading" class="co-loading"> · Loading…</span>
    </div>

    <div class="co-doc">
      <SgIncorporationPostPackageDoc ref="docRef" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn
        color="primary"
        class="text-none"
        :loading="creating"
        :disabled="!effectiveTitle || creating || entityLoading"
        @click="createTask"
      >
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.sg-inc-post-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sg-inc-post-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-entity { font-size: 12.5px; color: #42506a; }
.co-loading { color: #1B84FF; font-weight: 700; }
.co-doc { min-height: 200px; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #b42318; }
</style>
